import React, { useState, useEffect } from 'react';
import { Plus, Eye, Edit2, Trash2, MapPin, ArrowLeft } from 'lucide-react';
import toast from 'react-hot-toast';
import DataTable from '../../../components/common/DataTable';
import MainAreaForm from './MainAreaForm';
import mainAreaService from '../../../services/mainAreaService';

const MainAreaList = () => {
  const [view, setView] = useState('list'); // 'list', 'add', 'edit', 'detail'
  const [selectedMainArea, setSelectedMainArea] = useState(null);
  const [mainAreas, setMainAreas] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Pagination & Sorting State
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sortConfig, setSortConfig] = useState({ sortBy: 'nama', direction: 'ASC' });
  const [searchValue, setSearchValue] = useState('');

  // Fetch main areas on mount
  useEffect(() => {
    fetchMainAreas();
  }, []);

  // Fetch all main areas
  const fetchMainAreas = async () => {
    setIsLoading(true);
    try {
      const data = await mainAreaService.getAllMainAreas();
      setMainAreas(data);
    } catch (error) {
      console.error('Error fetching main areas:', error);
      // Error already handled by axios interceptor with toast
    } finally {
      setIsLoading(false);
    }
  };

  // Filter main areas based on search
  const filteredMainAreas = mainAreas.filter(mainArea =>
    mainArea.nama.toLowerCase().includes(searchValue.toLowerCase()) ||
    mainArea.ibukota.toLowerCase().includes(searchValue.toLowerCase()) ||
    mainArea.pulau.toLowerCase().includes(searchValue.toLowerCase()) ||
    (mainArea.description && mainArea.description.toLowerCase().includes(searchValue.toLowerCase()))
  );

  // Sort main areas
  const sortedMainAreas = [...filteredMainAreas].sort((a, b) => {
    const aValue = a[sortConfig.sortBy];
    const bValue = b[sortConfig.sortBy];
    
    if (aValue < bValue) return sortConfig.direction === 'ASC' ? -1 : 1;
    if (aValue > bValue) return sortConfig.direction === 'ASC' ? 1 : -1;
    return 0;
  });

  // Paginate main areas
  const startIndex = currentPage * pageSize;
  const paginatedMainAreas = sortedMainAreas.slice(startIndex, startIndex + pageSize);

  // Define columns for DataTable
  const columns = [
    {
      key: 'no',
      label: 'No',
      sortable: false,
      width: 'w-16',
      render: (row, index) => (
        <span className="font-medium text-gray-900">
          {startIndex + index + 1}
        </span>
      )
    },
    {
      key: 'nama',
      label: 'Nama Provinsi',
      sortable: true,
      render: (row) => (
        <div>
          <div className="font-semibold text-gray-900">{row.nama}</div>
          <div className="text-xs text-gray-500 mt-1">ID: {row.id}</div>
        </div>
      )
    },
    {
      key: 'ibukota',
      label: 'Ibukota',
      sortable: true,
      render: (row) => (
        <span className="text-sm text-gray-700">{row.ibukota}</span>
      )
    },
    {
      key: 'pulau',
      label: 'Pulau',
      sortable: true,
      render: (row) => (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          {row.pulau}
        </span>
      )
    },
    {
      key: 'koefisienHarga',
      label: 'Koefisien Harga',
      sortable: true,
      render: (row) => (
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${
            row.koefisienHarga >= 1.0 
              ? 'bg-red-100 text-red-800' 
              : 'bg-green-100 text-green-800'
          }`}>
            {parseFloat(row.koefisienHarga).toFixed(4)}
          </span>
          {row.koefisienHarga >= 1.0 && (
            <span className="text-xs text-gray-500">↑ Higher</span>
          )}
          {row.koefisienHarga < 1.0 && (
            <span className="text-xs text-gray-500">↓ Lower</span>
          )}
        </div>
      )
    },
    {
      key: 'description',
      label: 'Description',
      sortable: false,
      nowrap: false,
      render: (row) => (
        <span className="text-sm text-gray-600">
          {row.description ? (
            row.description.length > 50 
              ? row.description.substring(0, 50) + '...' 
              : row.description
          ) : (
            <span className="text-gray-400 italic">No description</span>
          )}
        </span>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      sortable: false,
      render: (row) => (
        <div className="flex gap-2">
          <button
            onClick={() => handleView(row)}
            className="text-blue-600 hover:text-blue-800 p-2 hover:bg-blue-50 rounded-lg transition-colors"
            title="View Details"
          >
            <Eye className="w-5 h-5" />
          </button>
          <button
            onClick={() => handleEdit(row)}
            className="text-green-600 hover:text-green-800 p-2 hover:bg-green-50 rounded-lg transition-colors"
            title="Edit"
          >
            <Edit2 className="w-5 h-5" />
          </button>
          <button
            onClick={() => handleDelete(row)}
            className="text-red-600 hover:text-red-800 p-2 hover:bg-red-50 rounded-lg transition-colors"
            title="Delete"
          >
            <Trash2 className="w-5 h-5" />
          </button>
        </div>
      )
    }
  ];

  // Handlers
  const handleAddNew = () => {
    setView('add');
    setSelectedMainArea(null);
  };

  const handleView = (mainArea) => {
    setSelectedMainArea(mainArea);
    setView('detail');
  };

  const handleEdit = (mainArea) => {
    setSelectedMainArea(mainArea);
    setView('edit');
  };

  const handleDelete = async (mainArea) => {
    if (window.confirm(`Apakah Anda yakin ingin menghapus ${mainArea.nama}?`)) {
      try {
        await mainAreaService.softDeleteMainArea(mainArea.id);
        toast.success('Main area berhasil dihapus!');
        fetchMainAreas(); // Refresh list
      } catch (error) {
        console.error('Error deleting main area:', error);
        // Error already handled by axios interceptor
      }
    }
  };

  const handleBackToList = () => {
    setView('list');
    setSelectedMainArea(null);
    fetchMainAreas(); // Refresh list when coming back
  };

  const handleSort = (newSortConfig) => {
    setSortConfig(newSortConfig);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(0);
  };

  const handleSearch = (value) => {
    setSearchValue(value);
    setCurrentPage(0);
  };

  // Render Form View
  if (view === 'add' || view === 'edit') {
    return (
      <MainAreaForm
        onBack={handleBackToList}
        mainAreaData={selectedMainArea}
        mode={view}
      />
    );
  }

  // Render Detail View
  if (view === 'detail' && selectedMainArea) {
    return (
      <div className="space-y-6">
        {/* Page Header */}
        <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={handleBackToList}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-800">Detail Main Area</h1>
                <p className="text-gray-600 mt-1">Informasi lengkap main area</p>
              </div>
            </div>
            <button
              onClick={() => handleEdit(selectedMainArea)}
              className="bg-red-600 hover:bg-red-700 text-white font-medium px-6 py-3 rounded-lg transition-all shadow-md hover:shadow-lg flex items-center gap-2"
            >
              <Edit2 className="w-5 h-5" />
              Edit Main Area
            </button>
          </div>
        </div>

        {/* Detail Card */}
        <div className="bg-white rounded-lg shadow-md border border-gray-200">
          <div className="p-6">
            <h2 className="text-lg font-bold text-gray-800 mb-4">Informasi Main Area</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <label className="block text-sm font-semibold text-gray-500 mb-2">ID</label>
                <p className="text-base text-gray-900">{selectedMainArea.id}</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <label className="block text-sm font-semibold text-gray-500 mb-2">Nama Provinsi</label>
                <p className="text-lg font-medium text-gray-900">{selectedMainArea.nama}</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <label className="block text-sm font-semibold text-gray-500 mb-2">Ibukota</label>
                <p className="text-base text-gray-900">{selectedMainArea.ibukota}</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <label className="block text-sm font-semibold text-gray-500 mb-2">Pulau</label>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  {selectedMainArea.pulau}
                </span>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <label className="block text-sm font-semibold text-gray-500 mb-2">Koefisien Harga</label>
                <div className="flex items-center gap-2">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${
                    selectedMainArea.koefisienHarga >= 1.0 
                      ? 'bg-red-100 text-red-800' 
                      : 'bg-green-100 text-green-800'
                  }`}>
                    {parseFloat(selectedMainArea.koefisienHarga).toFixed(4)}
                  </span>
                  {selectedMainArea.koefisienHarga >= 1.0 ? (
                    <span className="text-sm text-gray-600">(Higher than base price)</span>
                  ) : (
                    <span className="text-sm text-gray-600">(Lower than base price)</span>
                  )}
                </div>
              </div>
              {selectedMainArea.description && (
                <div className="md:col-span-2 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <label className="block text-sm font-semibold text-gray-500 mb-2">Description</label>
                  <p className="text-base text-gray-900">{selectedMainArea.description}</p>
                </div>
              )}
            </div>
          </div>

          {/* Audit Trail Section */}
          <div className="border-t border-gray-200 p-6">
            <h2 className="text-lg font-bold text-gray-800 mb-4">Audit Trail</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <label className="block text-xs font-semibold text-blue-700 mb-1">Created At</label>
                <p className="text-sm text-gray-900">
                  {new Date(selectedMainArea.createdAt).toLocaleString('id-ID')}
                </p>
                <p className="text-xs text-gray-600 mt-1">By: {selectedMainArea.createdBy}</p>
              </div>
              {selectedMainArea.updatedAt && (
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <label className="block text-xs font-semibold text-green-700 mb-1">Updated At</label>
                  <p className="text-sm text-gray-900">
                    {new Date(selectedMainArea.updatedAt).toLocaleString('id-ID')}
                  </p>
                  <p className="text-xs text-gray-600 mt-1">By: {selectedMainArea.updatedBy}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render List View (Default) - Using DataTable Component
  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
        <div className="flex items-center justify-between">     
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-700 rounded-lg flex items-center justify-center shadow-md">
              <MapPin className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Main Area / Province & Coefficient Harga Dasar</h1>
              <p className="text-gray-600 mt-1">Halaman untuk mengelola Main Area Project dan Koefisien Harga Dasar</p>
            </div>
          </div>
          <button 
            onClick={handleAddNew}
            className="bg-red-600 hover:bg-red-700 text-white font-medium px-6 py-3 rounded-lg transition-all shadow-md hover:shadow-lg flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Add Main Area
          </button>
        </div>
      </div>

      {/* DataTable Component */}
      <DataTable
        columns={columns}
        data={paginatedMainAreas}
        currentPage={currentPage}
        pageSize={pageSize}
        totalElements={filteredMainAreas.length}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onSort={handleSort}
        sortConfig={sortConfig}
        isLoading={isLoading}
        onSearch={handleSearch}
        searchValue={searchValue}
        searchPlaceholder="Search by province, capital, island, or description..."
        enableQuickJump={true}
        customPageSizes={[5, 10, 25, 50]}
        striped={false}
        headerBgColor="bg-gradient-to-r from-red-600 to-red-500"
        headerTextColor="text-white"
        hoverColor="hover:bg-red-50"
      />
    </div>
  );
};

export default MainAreaList;